/**
 * @see app/lib/government_id_class.rb for definitions.
 */
export const GOVERNMENT_ID_CLASSES = [
  'cccd',
  'cct',
  'cid',
  'dl',
  'hic',
  'foid',
  'id',
  'ipp',
  'keyp',
  'ltpass',
  'mid',
  'munid',
  'myn',
  'nbi',
  'nric',
  'ofw',
  'pan',
  'pid',
  'pp',
  'ppc',
  'pr',
  'rp',
  'sss',
  'td',
  'tribalid',
  'umid',
  'us-id',
  'vid',
  'visa',
  'wp',
] as const;
/**
 * If `undefined`, then the user selected "unknown ID type".
 * These must be a subset of coverage.json in persona-scanner. Ideally, we would infer types directly from the JSON but that
 * requires more setup. Chose to make this a union type to better fit what we'd have if we used the JSON file's keys.
 */
export type GovernmentIdClass = (typeof GOVERNMENT_ID_CLASSES)[number] | undefined;

/**
 * Special categories worth calling about the submitted identity document.
 *
 * NOTE: Keep in sync with app/models/verification/government_id.rb::Designations
 */
export type GovernmentIdDesignation =
  | 'commercial'
  | 'duplicate'
  | 'global_entry'
  | 'learners'
  | 'limited_term'
  | 'nexus'
  | 'permanent_resident'
  | 'provisional'
  | 'real_id'
  | 'restricted'
  | 'temporary'
  | 'veteran'
  | 'work_not_permitted';

export const GOVERNMENT_ID_DISPLAY_STRINGS: Record<
  Exclude<GovernmentIdClass, undefined>,
  string
> = {
  cccd: 'Citizen ID',
  cct: 'Citizen certificate',
  cid: 'Consular ID',
  dl: 'Driver license',
  hic: 'Healthcare Insurance card',
  foid: 'Foreigner ID',
  id: 'National ID',
  ipp: 'Internal passport',
  keyp: 'Keypass ID',
  ltpass: 'Long-Term pass card',
  mid: 'Military ID',
  munid: 'Municipal ID',
  myn: 'My Number card',
  nbi: 'NBI Clearance',
  nric: 'NRIC',
  ofw: 'OFW ID',
  pan: 'PAN card',
  pid: 'Postal ID',
  pp: 'Passport',
  ppc: 'Passport card',
  pr: 'Permanent resident card',
  rp: 'Residency permit',
  sss: 'SSS ID',
  td: 'Travel Document',
  tribalid: 'Tribal ID',
  umid: 'UMID',
  'us-id': 'US ID',
  vid: 'Voter ID',
  visa: 'Visa',
  wp: 'Work permit',
};

export const GOVERNMENT_ID_DESIGNATION_DISPLAY_STRINGS: Record<GovernmentIdDesignation, string> = {
  commercial: 'Commercial',
  duplicate: 'Duplicate',
  global_entry: 'Global Entry',
  learners: 'Learners Permit',
  limited_term: 'Limited Term',
  nexus: 'NEXUS',
  permanent_resident: 'Permanent Resident',
  provisional: 'Provisional',
  real_id: 'REAL ID',
  restricted: 'Restricted',
  temporary: 'Temporary',
  veteran: 'Veteran',
  work_not_permitted: 'Work not permitted',
};

export const GOVERNMENT_ID_DESIGN_VERSION = {
  V0: 0, // original design for Goverment ID step
  V1: 1, // covers all features originally gated by flipper inquiry:enable-govid-redesign
};

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export function isGovernmentIdClass(string: string): string is GovernmentIdClass {
  return Object.keys(GOVERNMENT_ID_DISPLAY_STRINGS).includes(string);
}
